import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { WithWrite } from '../../lib/rbac';
import { settingsApi, type ModuleRow } from '../../services/settings';

export function ModulesPage() {
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { error, success } = useToast();

  const load = useCallback(async () => {
    try {
      const res = await settingsApi.getModules();
      setModules(res.modules);
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte hämta moduler');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleVisible = async (moduleKey: string, isVisible: boolean) => {
    try {
      await settingsApi.updateBusinessModule(moduleKey, { isVisible });
      success(isVisible ? 'Modul visas i menyn' : 'Modul dold');
      load();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte uppdatera');
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Laddar...</p>;

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Moduler</h2>
      <p className="text-sm text-gray-600">
        Styr vilka moduler som visas i menyn. Planen styr om modulen är aktiverad.
      </p>
      <div className="border border-gray-200 rounded overflow-hidden">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">Modul</th>
              <th className="text-left px-2 py-1.5 font-medium">Beskrivning</th>
              <th className="text-left px-2 py-1.5 font-medium">Min plan</th>
              <th className="text-left px-2 py-1.5 font-medium">Aktiverad (plan)</th>
              <WithWrite moduleCode="settings">
                <th className="text-left px-2 py-1.5 font-medium">Synlig i meny</th>
              </WithWrite>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.moduleKey} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-2 py-1 font-medium">{m.name}</td>
                <td className="px-2 py-1 text-gray-600">{m.description ?? '-'}</td>
                <td className="px-2 py-1">{m.minPlanCode}</td>
                <td className="px-2 py-1">{m.enabled ? 'Ja' : 'Nej'}</td>
                <WithWrite moduleCode="settings">
                  <td className="px-2 py-1">
                    <button
                      type="button"
                      disabled={!m.enabled}
                      onClick={() => handleToggleVisible(m.moduleKey, m.isVisible ? false : true)}
                      className={`px-2 py-0.5 rounded text-sm ${
                        !m.enabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : m.isVisible
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {!m.enabled ? 'Ej tillgänglig' : m.isVisible ? 'Synlig' : 'Dold'}
                    </button>
                  </td>
                </WithWrite>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

